import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2pdf from 'html2pdf.js';
import { getAIExplanation } from '../api';
import Lottie from 'lottie-react';
import aiAnswerAnimation from '../animations/generate_ai_answer.json';
import { trackEvent } from "../utils/analytics";
import AIExplanationRenderer from './AIExplanationRenderer';

function QuestionViewer({ questionPaperText, meta }) {
  const allQuestionsRef = useRef();

  // ── Parse: accept object directly, no string operations ──
  const paper = useMemo(() => {
    if (!questionPaperText) return null;
    if (typeof questionPaperText === 'object' && questionPaperText !== null) {
      if (questionPaperText.sections && Array.isArray(questionPaperText.sections)) {
        return questionPaperText;
      }
    }
    console.error('[QuestionViewer] Invalid paper data:', typeof questionPaperText);
    return null;
  }, [questionPaperText]);

  // ── Flatten sections → questions once ──
  const flatQuestions = useMemo(() => {
    if (!paper) return [];
    const all = [];
    let qNum = 1;
    for (const section of paper.sections || []) {
      for (const q of section.questions || []) {
        all.push({ ...q, sectionName: section.name, sectionInstruction: section.instruction, globalId: qNum++ });
      }
    }
    return all;
  }, [paper]);

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [rateLimitReached, setRateLimitReached] = useState(false);

  useEffect(() => {
    if (paper && flatQuestions.length > 0) {
      trackEvent("generated_paper_viewed", {
        board: meta.board,
        grade: meta.grade,
        subject: meta.subject,
        question_count: flatQuestions.length
      });
    }
  }, []);

  const isFinalPage = index === flatQuestions.length;
  const currentQ = flatQuestions[index] ?? null;

  // ── AI Explanation (MCQ-aware) ──
  const handleAnswer = async () => {
    if (!currentQ || answers[index]) return;
    trackEvent("ai_explanation_requested", {
      question_id: currentQ.globalId,
      question_no: `Q${currentQ.globalId}`,
      board: meta.board,
      grade: meta.grade,
      subject: meta.subject,
      marks: currentQ.marks,
      source: "generated_paper"
    });
    setLoading(true);
    try {
      const isMCQ = currentQ.type === 'mcq' && currentQ.options && currentQ.options.length >= 2;
      const response = await getAIExplanation({
        text: currentQ.text,
        options: isMCQ ? currentQ.options.map((opt, i) => ({ label: String.fromCharCode(65 + i), text: opt })) : undefined,
        is_mcq: isMCQ,
        marks: currentQ.marks,
        board: meta.board,
        grade: meta.grade,
        subject: meta.subject,
        year: meta.year
      });
      setAnswers(prev => ({ ...prev, [index]: response.ai_explanation }));
      trackEvent("ai_explanation_completed", {
        question_id: currentQ.globalId,
        question_no: `Q${currentQ.globalId}`,
        board: meta.board,
        grade: meta.grade,
        subject: meta.subject,
        source: "generated_paper"
      });
    } catch (err) {
      if (err?.response?.status === 429) {
        setRateLimitReached(true);
        trackEvent("ai_limit_reached", {
          feature: "generated_paper_explanation",
          board: meta.board,
          grade: meta.grade,
          subject: meta.subject
        });
        trackEvent("login_prompt_shown", {
          reason: "ai_limit",
          feature: "generated_paper_explanation"
        });
        return;
      }
      trackEvent("ai_explanation_failed", {
        question_id: currentQ.globalId,
        board: meta.board,
        grade: meta.grade,
        subject: meta.subject,
        source: "generated_paper"
      });
      setAnswers(prev => ({ ...prev, [index]: "Something went wrong. Please try again." }));
    }
    setLoading(false);
  };

  // ── PDF download ──
  const downloadFullPDF = async () => {
    if (!allQuestionsRef.current) return;
    trackEvent("paper_downloaded", {
      board: meta.board,
      grade: meta.grade,
      subject: meta.subject,
      format: "pdf",
      source: "generated_paper"
    });
    const opt = { margin: 0.5, filename: `${meta.syllabus}_${meta.grade}_${meta.subject}_paper.pdf`, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2 }, jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' } };
    await html2pdf().set(opt).from(allQuestionsRef.current).save();
  };

  // ── Error state ──
  if (!paper) {
    return (
      <div style={{ padding: '60px', textAlign: 'center' }}>
        <h2 style={{ color: '#1e293b', fontSize: '20px' }}>No question paper data</h2>
        <p style={{ color: '#64748b', marginTop: '8px' }}>The paper could not be loaded. Please try generating again.</p>
      </div>
    );
  }

  if (flatQuestions.length === 0) {
    return (
      <div style={{ padding: '60px', textAlign: 'center' }}>
        <h2 style={{ color: '#1e293b', fontSize: '20px' }}>Failed to generate question paper.</h2>
        <p style={{ color: '#64748b', marginTop: '8px' }}>Please try again.</p>
        {paper.debug && <p style={{ color: '#ef4444', marginTop: '8px', fontSize: '13px' }}>{paper.message}</p>}
      </div>
    );
  }

  if (rateLimitReached) {
    return (
      <div style={{ padding: '60px', textAlign: 'center' }}>
        <h2 style={{ color: '#1e293b' }}>Free AI Limit Reached</h2>
        <p style={{ color: '#64748b' }}>Create a free account to continue.</p>
        <div style={{ marginTop: '25px' }}>
          <button onClick={() => window.location.href = "/signup"} style={{ padding: "12px 22px", fontSize: "16px", borderRadius: "8px", backgroundColor: "#10b981", color: "#fff", border: "none", cursor: "pointer", marginRight: "12px" }}>Create Free Account</button>
          <button onClick={() => window.location.href = "/login"} style={{ padding: "12px 22px", fontSize: "16px", borderRadius: "8px", backgroundColor: "#6366f1", color: "#fff", border: "none", cursor: "pointer" }}>Login</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1e293b', margin: 0 }}>Question Viewer</h2>
          <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
            <strong>{meta.board}</strong> • Grade {meta.grade} • {meta.subject} • {meta.term}
          </p>
        </div>
        <div style={{ fontSize: '13px', color: '#6366f1', fontWeight: 600, background: 'rgba(99,102,241,0.1)', padding: '6px 12px', borderRadius: '8px' }}>
          Question {index + 1} of {flatQuestions.length}
        </div>
      </div>

      {isFinalPage ? (
        <>
          <div style={{ textAlign: 'right', marginBottom: '20px' }}>
            <motion.button onClick={downloadFullPDF} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{ padding: '12px 24px', fontSize: '14px', fontWeight: 600, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              Download PDF
            </motion.button>
          </div>
          <div ref={allQuestionsRef} style={{ background: '#fafbfc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            {paper.sections?.map((section, sIdx) => (
              <div key={sIdx} style={{ marginBottom: '20px' }}>
                <div style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>
                  {section.name}{section.instruction ? ` — ${section.instruction}` : ''}
                </div>
                {section.questions?.map((q, qIdx) => (
                  <div key={qIdx} style={{ padding: '12px 16px', marginBottom: '8px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b', lineHeight: '1.6' }}>
                    <span style={{ fontWeight: 700, color: '#6366f1', marginRight: '8px' }}>Q{q.id}.</span>
                    {q.text}
                    {q.marks != null && <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: '4px' }}>[{q.marks} mark{q.marks > 1 ? 's' : ''}]</span>}
                    {q.type === 'mcq' && q.options && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {q.options.map((opt, i) => <div key={i} style={{ fontSize: '13px', color: '#475569' }}>({String.fromCharCode(65 + i)}) {opt}</div>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div key={index} initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -50, opacity: 0 }} transition={{ duration: 0.3 }}
            style={{ padding: '24px', background: '#fafbfc', borderRadius: '16px', marginTop: '10px', minHeight: '120px', border: '1px solid #e2e8f0' }}>
            {/* Section badge */}
            {currentQ.sectionName && (
              <div style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, marginBottom: '12px', display: 'inline-block' }}>
                {currentQ.sectionName}{currentQ.sectionInstruction ? ` — ${currentQ.sectionInstruction}` : ''}
              </div>
            )}
            {/* Question text */}
            <div style={{ fontSize: '15px', color: '#1e293b', lineHeight: '1.7' }}>
              <span style={{ fontWeight: 700, color: '#6366f1', marginRight: '8px' }}>Q{currentQ.globalId}. </span>
              {currentQ.text}
              {currentQ.marks != null && (
                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: '4px' }}>
                  [{currentQ.marks} mark{currentQ.marks > 1 ? 's' : ''}]
                </span>
              )}
            </div>
            {/* MCQ options */}
            {currentQ.type === 'mcq' && currentQ.options && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {currentQ.options.map((opt, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: '#f1f5f9', borderRadius: '8px', fontSize: '14px', color: '#334155', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontWeight: 600, color: '#6366f1', marginRight: '8px' }}>({String.fromCharCode(65 + i)})</span>
                    {opt}
                  </div>
                ))}
              </div>
            )}
            {/* Image placeholder */}
            {currentQ.image && (
              <div style={{ marginTop: '12px', padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', fontSize: '13px', color: '#0369a1', fontStyle: 'italic' }}>
                {currentQ.image}
              </div>
            )}
            {/* AI Answer */}
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                <Lottie animationData={aiAnswerAnimation} loop style={{ width: 150, height: 150 }} />
              </div>
            ) : answers[index] ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                style={{ marginTop: '20px', background: '#f8fafc', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#059669', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>🤖</span> AI Explanation
                </div>
                <AIExplanationRenderer content={answers[index]} />
              </motion.div>
            ) : null}
          </motion.div>
        </AnimatePresence>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', gap: '12px' }}>
        <motion.button disabled={index === 0} onClick={() => {
          trackEvent("previous_question", {
            question_id: currentQ?.globalId,
            question_no: `Q${currentQ?.globalId}`,
            from_index: index,
            source: "generated_paper"
          });
          setIndex(i => i - 1);
        }} whileHover={index > 0 ? { scale: 1.02 } : {}} whileTap={index > 0 ? { scale: 0.98 } : {}}
          style={{ padding: '12px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: '1px solid #e2e8f0', cursor: index === 0 ? 'default' : 'pointer', background: '#f9fafb', color: index === 0 ? '#cbd5e1' : '#475569', flex: 1 }}>
          Previous
        </motion.button>

        {!isFinalPage && (
          <motion.button
            onClick={handleAnswer}
            disabled={loading || !!answers[index]}
            whileHover={!loading && !answers[index] ? { scale: 1.02 } : {}}
            whileTap={!loading && !answers[index] ? { scale: 0.98 } : {}}
            style={{
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: 600,
              borderRadius: '10px',
              border: 'none',
              cursor: loading || answers[index] ? 'default' : 'pointer',
              background: answers[index]
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              color: 'white',
              boxShadow: answers[index]
                ? '0 4px 15px rgba(16,185,129,0.3)'
                : '0 4px 15px rgba(99,102,241,0.3)',
              opacity: loading && !answers[index] ? 0.7 : 1,
              flex: 1,
            }}
          >
            {loading && !answers[index] ? 'Thinking...' : answers[index] ? '✓ Answered' : 'AI Answer'}
          </motion.button>
        )}

        <motion.button onClick={isFinalPage ? downloadFullPDF : () => {
          trackEvent("next_question", {
            question_id: currentQ?.globalId,
            question_no: `Q${currentQ?.globalId}`,
            from_index: index,
            source: "generated_paper"
          });
          setIndex(i => i + 1);
        }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          style={{ padding: '12px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer', background: isFinalPage ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', boxShadow: isFinalPage ? '0 4px 15px rgba(16,185,129,0.3)' : '0 4px 15px rgba(99,102,241,0.3)', flex: 1 }}>
          {isFinalPage ? 'View Full Paper' : 'Next'}
        </motion.button>
      </div>
    </div>
  );
}

export default QuestionViewer;
